/**
 * Sistema de permisos y roles para el área de administración
 */

export type RolClave = "ADMINISTRADOR" | "SOPORTE" | "CONSULTA";
export type PermissionType = "READ" | "WRITE" | "NONE";

/**
 * Matriz de permisos por roles
 * Especifica qué secciones/módulos puede ver cada rol
 */
export const PERMISSION_MATRIX: Record<string, RolClave[]> = {
  // Solo ADMINISTRADOR
  "distributor-generation": ["ADMINISTRADOR"],
  "distributor-constance": ["ADMINISTRADOR"],
  "serial-generation": ["ADMINISTRADOR"],
  "batch-list": ["ADMINISTRADOR"],
  "certificates-list": ["ADMINISTRADOR"],
  "serial-management": ["ADMINISTRADOR"],

  // ADMINISTRADOR y SOPORTE
  "user-importer": ["ADMINISTRADOR", "SOPORTE"],

  // ADMINISTRADOR, SOPORTE y CONSULTA (con diferentes permisos)
  "user-edit": ["ADMINISTRADOR", "SOPORTE", "CONSULTA"],
  "search-user-courses": ["ADMINISTRADOR", "SOPORTE", "CONSULTA"],
};

/**
 * Roles que tienen permiso de escritura (W)
 * Los roles no listados aquí tienen solo lectura (R)
 */
export const WRITE_ONLY_ROLES: Record<string, RolClave[]> = {
  "user-edit": ["ADMINISTRADOR", "SOPORTE"],
  "search-user-courses": ["ADMINISTRADOR", "SOPORTE"],
};

/**
 * Verifica si un rol tiene acceso a un módulo específico
 * @param rolClave - Clave del rol del usuario
 * @param moduleKey - Clave del módulo a acceder
 * @returns true si tiene acceso, false si no
 */
export const hasAccessToModule = (
  rolClave: RolClave | undefined,
  moduleKey: string
): boolean => {
  if (!rolClave) return false;
  const allowedRoles = PERMISSION_MATRIX[moduleKey];
  if (!allowedRoles) return true; // Si no hay restricción, permite acceso
  return allowedRoles.includes(rolClave);
};

/**
 * Verifica si un rol tiene permiso de escritura en un módulo
 * @param rolClave - Clave del rol del usuario
 * @param moduleKey - Clave del módulo
 * @returns true si tiene permiso de escritura, false si solo lectura
 */
export const hasWritePermission = (
  rolClave: RolClave | undefined,
  moduleKey: string
): boolean => {
  if (!rolClave) return false;
  const writeRoles = WRITE_ONLY_ROLES[moduleKey];
  if (!writeRoles) return true; // Si no hay restricción de escritura (admins tienen acceso completo)
  return writeRoles.includes(rolClave);
};

/**
 * Obtiene el tipo de permiso para un usuario en un módulo
 * @param rolClave - Clave del rol del usuario
 * @param moduleKey - Clave del módulo
 * @returns "WRITE", "READ" o "NONE"
 */
export const getPermissionType = (
  rolClave: RolClave | undefined,
  moduleKey: string
): PermissionType => {
  if (!hasAccessToModule(rolClave, moduleKey)) {
    return "NONE";
  }
  if (hasWritePermission(rolClave, moduleKey)) {
    return "WRITE";
  }
  return "READ";
};

/**
 * Obtiene todos los módulos accesibles para un rol
 * @param rolClave - Clave del rol
 * @returns Array de claves de módulos accesibles
 */
export const getAccessibleModules = (rolClave: RolClave | undefined): string[] => {
  if (!rolClave) return [];
  return Object.entries(PERMISSION_MATRIX)
    .filter(([_key, allowedRoles]) => allowedRoles.includes(rolClave))
    .map(([key]) => key);
};
